import { useEffect, useRef, useState } from "react"
import { flushSync } from "react-dom"
import { router, usePage } from "@inertiajs/react"
import { toast } from "sonner"

import AppShell from "@/components/tuw/AppShell"
import { useShellActivity } from "@/components/tuw/ShellActivity"
import OrderHeader from "@/components/tuw/OrderHeader"
import OrderTabs from "@/components/tuw/OrderTabs"
import OrderModals from "@/components/tuw/order/OrderModals"
import SharedViewStrip from "@/components/tuw/order/SharedViewStrip"
import OutlinePromptStrip from "@/components/tuw/order/OutlinePromptStrip"
import { BriefStatusStrips } from "@/components/tuw/openHouse/BriefStatusStrip"
import RequestOutcomeStrip from "@/components/tuw/openHouse/RequestOutcomeStrip"
import { ORDER_MODAL_KEYS } from "@/components/tuw/order/actions"
import DocumentPanel from "@/components/tuw/order/DocumentPanel"
import ChatPanel from "@/components/tuw/order/ChatPanel"
import RevisionsPanel from "@/components/tuw/order/RevisionsPanel"
import DetailsPanel from "@/components/tuw/order/DetailsPanel"
import DesignPreviewPanel from "@/components/tuw/order/DesignPreviewPanel"
import IllustrationsPanel from "@/components/tuw/order/IllustrationsPanel"
import SharedFilesPanel from "@/components/tuw/order/SharedFilesPanel"
import WordProgress from "@/components/tuw/order/WordProgress"
import ProofPanel from "@/components/tuw/order/ProofPanel"
import AudioPanel from "@/components/tuw/order/AudioPanel"
import { csrfToken, postOrderAction } from "@/lib/orderActions"

// Tab key -> panel. The order type decides which keys are present (server-side),
// so only the relevant panels ever render.
const PANELS = {
  document: DocumentPanel,
  chat: ChatPanel,
  revisions: RevisionsPanel,
  details: DetailsPanel,
  design: DesignPreviewPanel,
  illus: IllustrationsPanel,
  files: SharedFilesPanel,
  proof: ProofPanel,
  audio: AudioPanel,
}

// Reflect the active tab into ?tab= without an Inertia visit, preserving
// Inertia's history state so the tab stays linkable and refresh-safe.
function syncTabUrl(tab) {
  if (typeof window === "undefined") return

  const url = new URL(window.location.href)
  if (url.searchParams.get("tab") === tab) return

  url.searchParams.set("tab", tab)
  window.history.replaceState(window.history.state, "", url)
}

// Atrium Stage 3 — one order-detail surface: a phase-driven OrderHeader + a
// context-aware tab rail, the active tab's panel, and the action dialogs. The
// header/menu actions and the modals submit to the Customer::Orders endpoints
// (real OrderActions services), then the page re-renders with the new state.
export default function OrderDetail({ order, initialTab }) {
  const tabs = order.tabs ?? []
  const [active, setActive] = useState(tabs.includes(initialTab) ? initialTab : tabs[0])
  const [tabBadges, setTabBadges] = useState(order.tabBadges ?? {})
  // Which Chat-tab thread is showing on a managed order (AU-40): the team chat,
  // or the customer's own line to their manager. Opens on the team unless there
  // is no team yet and there is a manager.
  // `?thread=manager` (the inbox's manager row) opens on the manager thread.
  const [chatThread, setChatThread] = useState(() => {
    if (!order.managerChat) return "team"
    const asked = new URLSearchParams(window.location.search).get("thread")
    return asked === "manager" || !order.team?.length ? "manager" : "team"
  })
  // The manager thread's unread count, kept apart from tabBadges.chat (the team
  // thread's) so each thread clears on its own; the Chat tab shows the sum.
  const [managerUnread, setManagerUnread] = useState(Number(order.managerChat?.unread) || 0)
  const markingRead = useRef(null)
  const panelRef = useRef(null)
  const { threads, refresh } = useShellActivity()
  // Raised while a read write has cleared the Chat badge but no post-write poll
  // answer has landed yet. Any shell data in flight during that window was read
  // from the pre-write database and would resurrect the count we just cleared,
  // so while the barrier is up the badge may fall but never rise. It comes down
  // when the forced refresh below settles, because that response is guaranteed
  // to postdate the write.
  const clearBarrier = useRef(false)
  // `modal` is { key, data } — data carries per-open context (e.g. which
  // narration chapter to request changes on); null means closed.
  const [modal, setModal] = useState(null)
  // True while an order action is on the wire, so the dialog's confirm button
  // greys out instead of inviting the second click that AU-35 came from.
  const [busy, setBusy] = useState(false)
  const [clientFlash, setClientFlash] = useState(null)
  const { flash } = usePage().props

  useEffect(() => {
    syncTabUrl(active)
  }, [active])

  // An accept the server refused because the Open House brief is unfinished
  // lands back here, on a fresh page or this one: the requests, which say so,
  // are (re)opened.
  useEffect(() => {
    if ((order.requestItems ?? []).some((request) => request.id === flash?.briefBlocked)) {
      openModal(ORDER_MODAL_KEYS.REVIEW_REQUESTS)
    }
  }, [flash?.briefBlocked])

  useEffect(() => {
    setTabBadges(order.tabBadges ?? {})
  }, [order.id, order.tabBadges?.chat])

  useEffect(() => {
    setManagerUnread(Number(order.managerChat?.unread) || 0)
  }, [order.id, order.managerChat?.unread])

  // Live Chat-tab badge (AQ-026). The shell's shared refresher already carries
  // this order's conversation among its recent threads, so an arriving message
  // raises the badge — and a clear on any other surface drops it — with no
  // navigation. The slice is the top 5 threads, so ABSENCE carries no
  // information: keep the last known value rather than inventing a zero.
  //
  // Read-only. The read write below stays the only thing that clears server-side.
  useEffect(() => {
    const conversationId = order.chat?.conversationId
    if (!conversationId || !threads) return

    const row = threads.find((thread) => thread.conversationId === conversationId)
    if (!row) return

    const next = Number(row.unread) || 0
    setTabBadges((current) => {
      const shown = Number(current.chat) || 0
      if (next === shown) return current
      if (next > shown && clearBarrier.current) return current
      return { ...current, chat: next }
    })
  }, [threads, order.chat?.conversationId])

  // The same live badge for the manager thread, under the same barrier.
  useEffect(() => {
    const conversationId = order.managerChat?.conversationId
    if (!conversationId || !threads) return

    const row = threads.find((thread) => thread.conversationId === conversationId)
    if (!row) return

    const next = Number(row.unread) || 0
    setManagerUnread((shown) => {
      if (next === shown) return shown
      if (next > shown && clearBarrier.current) return shown
      return next
    })
  }, [threads, order.managerChat?.conversationId])

  // Opening Chat advances this customer's participant row through the same
  // session endpoint Messages uses. The badge clears only after the server
  // confirms the write, then shared shell/inbox counts reload from server truth.
  useEffect(() => {
    const conversationId = order.chat?.conversationId
    const unread = Number(tabBadges.chat) || 0
    if (active !== "chat" || chatThread !== "team" || !conversationId || unread <= 0 || markingRead.current === conversationId) return

    markingRead.current = conversationId
    fetch(`/messages/conversations/${conversationId}/read`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken() },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Unable to mark project chat read")
        clearBarrier.current = true
        setTabBadges((current) => ({ ...current, chat: 0 }))
        router.reload({ only: ["order", "unreadMessages"], preserveScroll: true })
        // Close the window the barrier is holding: force a fresh read of shell
        // truth (a joined in-flight poll could predate this write) and lower the
        // barrier once it settles. A failed refresh lowers it too — the badge
        // then simply reconciles on the next tick rather than staying frozen.
        return refresh({ force: true }).finally(() => {
          clearBarrier.current = false
        })
      })
      .catch(() => {})
      .finally(() => {
        markingRead.current = null
      })
  }, [active, chatThread, order.chat?.conversationId, tabBadges.chat, refresh])

  // Showing the manager thread marks it read the same way; only the thread on
  // screen is ever marked, so switching threads never reads the other one away.
  useEffect(() => {
    const conversationId = order.managerChat?.conversationId
    if (active !== "chat" || chatThread !== "manager" || !conversationId || managerUnread <= 0 || markingRead.current === conversationId) return

    markingRead.current = conversationId
    fetch(`/messages/conversations/${conversationId}/read`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken() },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Unable to mark manager chat read")
        clearBarrier.current = true
        setManagerUnread(0)
        router.reload({ only: ["order", "unreadMessages"], preserveScroll: true })
        return refresh({ force: true }).finally(() => {
          clearBarrier.current = false
        })
      })
      .catch(() => {})
      .finally(() => {
        markingRead.current = null
      })
  }, [active, chatThread, order.managerChat?.conversationId, managerUnread, refresh])

  // Stripe redirects back here after a tip is confirmed (?tip=success +
  // redirect_status). Show the outcome, then strip Stripe's params from the URL.
  useEffect(() => {
    if (typeof window === "undefined") return

    const params = new URLSearchParams(window.location.search)
    const status = params.get("redirect_status")
    if (params.get("tip") !== "success" && !status) return

    setClientFlash(
      status === "failed"
        ? { alert: "That payment didn't go through, so no charge was made." }
        : { notice: "Thank you — your tip is on its way to your freelancer." }
    )

    ;["tip", "redirect_status", "payment_intent", "payment_intent_client_secret"].forEach((k) => params.delete(k))
    const url = new URL(window.location.href)
    url.search = params.toString()
    window.history.replaceState(window.history.state, "", url)
  }, [])

  // Surface the client-only flash (Stripe tip return) as a toast. The server
  // `flash` is owned by AppShell's FlashBridge, so it isn't toasted again here.
  useEffect(() => {
    if (!clientFlash) return
    if (clientFlash.alert) toast.error(clientFlash.alert)
    else if (clientFlash.notice) toast.success(clientFlash.notice)
  }, [clientFlash])

  const selectTab = (tab) => {
    if (tabs.includes(tab)) setActive(tab)
  }

  // A header action ("Message your team", "Upload your files", …) names the tab
  // it wants the customer to act in, but on a phone that panel sits below the
  // fold — the tab switched and nothing appeared to happen (AU-05). So a header
  // action also brings the panel into view. Deliberately NOT part of `selectTab`:
  // pressing a tab in the rail must leave the page exactly where it is.
  //
  // The scroll must finish inside this handler, and must not be animated.
  // Opening Chat marks the thread read, and that reload carries `preserveScroll`:
  // Inertia snapshots every [scroll-region] scrollTop when the reload RESPONSE
  // lands (@inertiajs/core :1078), then re-applies it with an instant `scrollTo`
  // one animation frame later (:1111). Since the previous slice the shell's
  // <main> is that one marked region — the element scrolled here. Completing
  // synchronously means this scroll is always already at rest when the snapshot
  // is taken, so the restore can only put the resting offset back.
  //
  // An animated scroll is still travelling when a fast round trip lands, and the
  // restore freezes it part-way down the screen — the AU-05 symptom itself. That
  // failure hides behind a slow local server, where the reload arrives after the
  // animation has finished; it shows up only in production, where the round trip
  // beats it. Do not reintroduce `behavior: "smooth"` here.
  const selectTabFromHeader = (tab) => {
    // Commit the requested panel BEFORE scrolling. The panel column is the same
    // element on every tab, but how far the shell CAN scroll is not: measuring
    // against the outgoing panel's height clamps the scroll short and strands
    // the new panel part-way down the screen.
    flushSync(() => selectTab(tab))
    // "instant" is explicit rather than the "auto" default so that adding
    // `scroll-behavior: smooth` to a stylesheet later cannot silently
    // reintroduce an interruptible animation here.
    panelRef.current?.scrollIntoView({ behavior: "instant", block: "start" })
  }
  // Opening or closing a dialog clears `busy`. It belongs to the dialog that
  // submitted, and the customer can walk away from that dialog mid-request
  // (Cancel, Escape, the close X) — without this, an abandoned request would
  // leave the NEXT dialog's confirm button greyed out until it settled. The
  // real duplicate-submit guard is in postOrderAction and is unaffected.
  const openModal = (key, data = null) => {
    setBusy(false)
    setModal({ key, data })
  }
  const closeModal = () => {
    setBusy(false)
    setModal(null)
  }

  // Submit an order action to its session endpoint, carrying the current tab so
  // the redirect lands back here, and closing the modal once it settles.
  const runAction = (path, data = {}) => {
    setBusy(true)
    postOrderAction(order.id, path, { ...data, tab: active }, {
      onDone: () => {
        setBusy(false)
        closeModal()
      },
    })
  }

  const Panel = PANELS[active] ?? DetailsPanel
  const chatUnread = Number(tabBadges.chat) || 0
  const badges = managerUnread ? { ...tabBadges, chat: chatUnread + managerUnread } : tabBadges

  return (
    <>
      <div className="px-[26px] pt-[22px] max-[860px]:px-4 max-[860px]:pt-4 max-[560px]:px-3 max-[560px]:pt-[14px]">
        <SharedViewStrip viewer={order.viewer} />
        <OutlinePromptStrip prompt={order.requirements?.prompt} onUpload={() => selectTabFromHeader("details")} />
        <BriefStatusStrips order={order} />
        <RequestOutcomeStrip
          outcome={order.requestOutcome}
          waiting={order.requestItems?.length ?? 0}
          onReview={() => openModal(ORDER_MODAL_KEYS.REVIEW_REQUESTS)}
        />
        <OrderHeader order={order} onOpenModal={openModal} onSelectTab={selectTabFromHeader} />
        <WordProgress progress={order.wordProgress} />
      </div>

      {/* Switchback (P8-H3). The rail/panel split lives at the design bundle's
          860, not Tailwind's `lg`: below it the rail becomes a horizontal strip
          above a full-width panel, so the children must stretch (`items-start`
          would shrink both to their content width in a column). */}
      <div className="flex flex-col items-start gap-[22px] px-[26px] pt-[22px] pb-10 min-[861px]:flex-row max-[860px]:items-stretch max-[860px]:gap-4 max-[860px]:px-4 max-[860px]:pt-4 max-[860px]:pb-9 max-[560px]:px-3 max-[560px]:pt-[14px] max-[560px]:pb-8">
        <OrderTabs
          tabs={tabs}
          active={active}
          onSelect={selectTab}
          primaryDot={order.primaryDot}
          badges={badges}
        />
        <div ref={panelRef} className="min-w-0 flex-1">
          <Panel
            order={order}
            onOpenModal={openModal}
            onSelectTab={selectTab}
            chatThread={chatThread}
            onSelectChatThread={setChatThread}
            chatUnread={chatUnread}
            managerUnread={managerUnread}
          />
        </div>
      </div>

      <OrderModals
        modal={modal?.key ?? null}
        modalData={modal?.data ?? null}
        onClose={closeModal}
        order={order}
        runAction={runAction}
        busy={busy}
      />
    </>
  )
}

OrderDetail.layout = (page) => {
  // Inertia (React 3.4) probes a layout resolver with the raw props object before
  // re-invoking it with the page element, so read the order from whichever shape
  // arrives (the props object has no `.props`; the element does).
  const props = page.props ?? page
  return (
    <AppShell active="Projects" crumb={["Projects", props.order?.title]}>
      {page}
    </AppShell>
  )
}
